/**
 * useTaskBreadcrumb — 4-hop breadcrumb chain for a task. DRR-126 Phase 8.
 *
 * Resolves: Task → TaskGroup (tg-*) → [Workflow (wf-*)] → Constellation → Mandate (rs-*)
 *
 * All hops except workflow_ref are derived from fields already stored on the task
 * at seed/creation time. workflow_ref is optional (null until indexed via constellation
 * definition_nodes).
 *
 * Data tier: T2. No clinical content. DRR-114 compliant.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ─── GraphQL query ────────────────────────────────────────────────────────────

const TASK_BREADCRUMB_QUERY = gql`
  query TaskBreadcrumb($taskId: String!) {
    taskBreadcrumb(taskId: $taskId) {
      taskId
      taskName
      taskStatus
      taskType
      taskGroupRef
      workflowRef
      constellationId
      mandateRef
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskBreadcrumbHop {
  /** Display label for this hop */
  label: string;
  /** The config ID or identifier (e.g., "tg-4-3-001", "rs-4-1-004") */
  ref: string;
  /** Hop type — determines navigation behavior */
  type: 'task' | 'task_group' | 'workflow' | 'constellation' | 'mandate';
  /** Whether this hop is present (null hops are shown as dimmed) */
  present: boolean;
}

export interface TaskBreadcrumbResult {
  taskId: string;
  taskName: string;
  taskStatus: string;
  taskType: string;
  /** Ordered hops from task → mandate (4-hop chain) */
  hops: TaskBreadcrumbHop[];
  loading: boolean;
  error?: Error;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTaskBreadcrumb(taskId: string | null | undefined): TaskBreadcrumbResult {
  const { data, loading, error } = useQuery(TASK_BREADCRUMB_QUERY, {
    variables: { taskId: taskId ?? '' },
    skip: !taskId,
    fetchPolicy: 'cache-first',
  });

  const result = useMemo<TaskBreadcrumbResult>(() => {
    // @ts-ignore — taskBreadcrumb not yet in generated types (query added ahead of schema codegen)
    const bc = data?.taskBreadcrumb;

    if (!bc) {
      return {
        taskId: taskId ?? '',
        taskName: '—',
        taskStatus: 'unknown',
        taskType: 'manual',
        hops: [],
        loading,
        error,
      };
    }

    const hops: TaskBreadcrumbHop[] = [
      {
        label: bc.taskName,
        ref: bc.taskId,
        type: 'task',
        present: true,
      },
      {
        label: bc.taskGroupRef
          ? bc.taskGroupRef.replace(/^tg-/, 'TG ').replace(/-/g, '·')
          : 'No Task Group',
        ref: bc.taskGroupRef ?? '',
        type: 'task_group',
        present: !!bc.taskGroupRef,
      },
      {
        label: bc.workflowRef
          ? bc.workflowRef.replace(/^wf-/, 'WF ').replace(/-/g, '·')
          : 'Workflow',
        ref: bc.workflowRef ?? '',
        type: 'workflow',
        present: !!bc.workflowRef,
      },
      {
        label: bc.constellationId
          ? bc.constellationId.replace(/^constellation-/, '').replace(/-/g, ' ')
          : 'No Constellation',
        ref: bc.constellationId ?? '',
        type: 'constellation',
        present: !!bc.constellationId,
      },
      {
        label: bc.mandateRef
          ? bc.mandateRef.replace(/^rs-/, 'RS ').replace(/-/g, '·')
          : 'No Mandate',
        ref: bc.mandateRef ?? '',
        type: 'mandate',
        present: !!bc.mandateRef,
      },
    ];

    return {
      taskId: bc.taskId,
      taskName: bc.taskName,
      taskStatus: bc.taskStatus,
      taskType: bc.taskType,
      hops,
      loading: false,
      error: undefined,
    };
  }, [data, loading, error, taskId]);

  return result;
}
